import type { ComponentChildren } from 'preact';
import { useMemo, useRef, useCallback, useState } from 'preact/hooks';
import { Ref } from 'preact';
import { forwardRef, useImperativeHandle } from 'preact/compat';
import { TabBarContext, TabBarContextMenuConfig } from '../UNSAFE_TabBarCommon';
import { isMobile } from '#utils/UNSAFE_clientUtils';
import { styles } from './themes/TabBarMixedStyles.css';
import { useTabBarMixed as useTabBar } from './useTabBarMixed';
import { REMOVABLE_ITEM_ATTRIBUTE, getItemAttribute } from '../hooks/PRIVATE_useTabBar/useTabBar';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { TabBarContextMenu } from '../UNSAFE_TabBar/TabBarContextMenu';
import { mergeProps } from '../utils/UNSAFE_mergeProps/mergeProps';
import { ConveyorBeltFocusContext } from '../UNSAFE_ConveyorBelt';

type UseTabBarOptions = Parameters<typeof useTabBar>[0];

/**
 * Component props expected to be pased to **TabBarMixed**.
 *
 * @see {@link TabBarMixed}
 */
type TabBarMixedProps<K extends string | number> = {
  /**
   * Component children that will be rendered within the **TabBarMixed**
   * root element.
   *
   * It is expected that **TabBarLayout** and **ConveyorBelt** elements are
   * provided. The same **TabBarItem**, **RemovableTabBarItem**,
   * and **OverflowTabBarItem** elements should also be used.
   */
  children?: ComponentChildren;
  /**
   * Callback fired when a tab item is removed.
   */
  onRemove?: UseTabBarOptions['onRemove'];
  /**
   * Callback fired when a tab item is selected.
   */
  onSelect?: UseTabBarOptions['onSelect'];
  /**
   * The item key of the selected tab item.
   */
  selection?: K;
  /**
   * The height of the the tab bar.
   *
   * @default "md"
   */
  size?: UseTabBarOptions['size'];
  /**
   * An aria-label which defines a string value that labels this TabBarMixed.
   * Either aria-label or aria-labelledby should be specified in order to make TabBarMixed accessible.
   */
  'aria-label'?: string;

  /**
   * An aria-labelledby which identifies the element(s) that labels this TabBarMixed.
   * Either aria-label or aria-labelledby should be specified in order to make TabBarMixed accessible.
   */
  'aria-labelledby'?: string;
};

export type FocusableHandle = {
  focus: () => void;
  blur: () => void;
};

/**
 * A navigation component that enables horizontal navigation between distinct content with a mixture of static and dynamic tabs.
 *
 * @param {TabBarMixedProps} props TabBarMixed component props.
 * @returns {JSX.Element} TabBarMixed component element.
 */
export const TabBarMixed = forwardRef(
  <K extends string | number>(
    props: TabBarMixedProps<K>,
    focusHandleRef?: Ref<FocusableHandle>
  ) => {
    const {
      children,
      onRemove,
      onSelect,
      selection,
      size,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby
    } = props;

    const rootRef = useRef<HTMLDivElement>(null);

    const isMobileDevice = isMobile();
    const defaultContextMenuConfig = useMemo<TabBarContextMenuConfig<string | number>>(() => {
      return {
        itemsRenderer: (context) => {
          //Only if context is available, it is a mobile device and it is removable item, we will return remove context menu item
          if (context) {
            const isRemovableItem =
              getItemAttribute(context.itemKey, rootRef.current!, REMOVABLE_ITEM_ATTRIBUTE) != null;
            if (isRemovableItem && isMobileDevice) {
              const defaultItems = context.defaultMenuItems;
              return <>{defaultItems}</>;
            }
          }
          return null;
        }
      };
    }, []);

    const {
      rootProps,
      tabBarContext,
      menuProps,
      contextMenuContext,
      contextMenuDescription,
      contextMenuDescriptionId,
      accStatusInfo
    } = useTabBar({
      class: styles.tabBarBase,
      edge: 'bottom',
      onRemove,
      onSelect,
      ref: rootRef,
      selection,
      size,
      children,
      contextMenuConfig: defaultContextMenuConfig
    });

    const isContextMenuAvailable = defaultContextMenuConfig != null && isMobileDevice;
    //Allows to call focus on rootRef without having to expose it
    useImperativeHandle(focusHandleRef!, () => ({
      focus: () => {
        if (rootRef.current) {
          rootRef.current.focus();
        }
      },
      blur: () => {
        if (rootRef.current) {
          rootRef.current.blur();
        }
      }
    }));

    const [isSuppressOverflowFocus, setIsSuppressOverflowFocus] = useState(false);

    const focusTabbarRoot = useCallback(() => {
      rootRef.current?.focus();
    }, []);

    // Begin a short window where overflow div should not be focusable.
    const beginTabPass = useCallback(() => {
      setIsSuppressOverflowFocus(true);
    }, []);

    // End that window after focus moves away.
    const endTabPass = useCallback(() => {
      setIsSuppressOverflowFocus(false);
    }, []);

    const onKeyDown = useCallback(
      (e: KeyboardEvent) => {
        // We only want to suppress overflow focus when the user is tabbing.
        if (e.key === 'Tab' && !e.shiftKey) {
          // Tell conveyor to make its overflow non-focusable (tabIndex = -1)
          beginTabPass?.();
        }
      },
      [beginTabPass]
    );

    const onBlur = useCallback(() => {
      // End that suppress overflow after focus moves away.
      endTabPass?.();
    }, [endTabPass]);

    const focusContext = useMemo(
      () => ({
        focusTabbarRoot,
        isSuppressOverflowFocus,
        beginTabPass,
        endTabPass
      }),
      [isSuppressOverflowFocus, focusTabbarRoot, beginTabPass, endTabPass]
    );

    const mergedRootProps = mergeProps(rootProps, { onKeyDown, onBlur });

    return (
      <ConveyorBeltFocusContext.Provider value={focusContext}>
        <TabBarContext.Provider value={tabBarContext}>
          <div aria-label={ariaLabel} aria-labelledby={ariaLabelledby} {...mergedRootProps}>
            {children}
          </div>
        </TabBarContext.Provider>
        {isContextMenuAvailable && contextMenuDescriptionId && contextMenuDescription && (
          <span id={contextMenuDescriptionId}>
            <HiddenAccessible>{contextMenuDescription}</HiddenAccessible>
          </span>
        )}
        {isContextMenuAvailable && accStatusInfo && <LiveRegion>{accStatusInfo}</LiveRegion>}
        {isContextMenuAvailable && (
          <TabBarContextMenu
            {...menuProps}
            contextMenuConfig={defaultContextMenuConfig}
            contextMenuContext={contextMenuContext!}
          />
        )}
      </ConveyorBeltFocusContext.Provider>
    );
  }
);
